import axios from "axios";
import jwt_decode from "jwt-decode";

// Salesforce (dealership portal) login with PKCE. Salesforce only proves who the user is: the code is
// exchanged by uep-auth-service, which checks the user's onboarding/role in UEP and returns UEP tokens.
// Exposes the subset of the keycloak-js API the portal uses, so callers work with either provider.

const PKCE_STORAGE_KEY = "uep.salesforce.pkce";

const appData = () => window.UepAppData || {};

const authApiBaseURL = () => {
  const { API_BASE_URL, BUSINESS_UNIT } = appData();
  return API_BASE_URL + (BUSINESS_UNIT === "CV" ? "/cv" : "") + "/auth/salesforce";
};

const base64Url = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const randomString = (byteLength) =>
  base64Url(window.crypto.getRandomValues(new Uint8Array(byteLength)));

const codeChallenge = async (verifier) =>
  base64Url(
    await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier))
  );

export class LoginError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const salesforceAuth = {
  token: undefined,
  tokenParsed: undefined,
  refreshToken: undefined,
  refreshTokenParsed: undefined,

  setTokens({ accessToken, refreshToken }) {
    this.token = accessToken;
    this.tokenParsed = jwt_decode(accessToken);
    this.refreshToken = refreshToken;
    this.refreshTokenParsed = jwt_decode(refreshToken);
  },

  clearTokens() {
    this.token = undefined;
    this.tokenParsed = undefined;
    this.refreshToken = undefined;
    this.refreshTokenParsed = undefined;
  },

  // Resolves true once logged in. Without a Salesforce callback in the URL it redirects to Salesforce
  // (which signs the user straight back in when they already have a dealership portal session).
  async init() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) {
      await this.login();
      return false;
    }

    const stored = JSON.parse(sessionStorage.getItem(PKCE_STORAGE_KEY) || "null");
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
    // Drop code/state from the address bar so they are not reused or leaked via history.
    window.history.replaceState(null, "", window.location.pathname);
    if (!stored || stored.state !== params.get("state")) {
      throw new LoginError("Login session expired or is invalid. Please log in again.");
    }

    try {
      const res = await axios.post(`${authApiBaseURL()}/token`, {
        code,
        codeVerifier: stored.verifier,
      });
      this.setTokens(res.data);
      return true;
    } catch (err) {
      const status = err.response && err.response.status;
      throw new LoginError(
        status === 403
          ? "You are not onboarded on UEP or your account is inactive. Please contact UEP admin."
          : "Salesforce login failed. Please retry or contact UEP admin.",
        status
      );
    }
  },

  async login() {
    const { SF_AUTHORIZE_URL, SF_CLIENT_ID, SF_REDIRECT_URI } = appData();
    const verifier = randomString(64);
    const state = randomString(16);
    sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify({ verifier, state }));
    const query = new URLSearchParams({
      response_type: "code",
      client_id: SF_CLIENT_ID,
      redirect_uri: SF_REDIRECT_URI,
      code_challenge: await codeChallenge(verifier),
      code_challenge_method: "S256",
      state,
    });
    window.location.assign(`${SF_AUTHORIZE_URL}?${query.toString()}`);
  },

  // minValidity is accepted for keycloak-js compatibility; the portal only ever forces a refresh.
  async updateToken() {
    if (!this.refreshToken) throw new LoginError("Not logged in");
    const res = await axios.post(`${authApiBaseURL()}/refresh`, {
      refreshToken: this.refreshToken,
    });
    this.setTokens(res.data);
    return true;
  },

  logout() {
    this.clearTokens();
    sessionStorage.removeItem(PKCE_STORAGE_KEY);
    const { SF_LOGOUT_URL } = appData();
    if (SF_LOGOUT_URL) {
      window.location.assign(SF_LOGOUT_URL);
    } else {
      this.login();
    }
  },
};

export default salesforceAuth;
